import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@shared/ui/primitives';
import { portalMcpApi, type McpTenantFeature } from '@/services/portalMcpApi';
import { McpAdminPageShell } from './McpAdminShell';

export function McpTenantFeaturesPage() {
  const [tenants, setTenants] = useState<McpTenantFeature[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<McpTenantFeature | null>(null);
  const [form, setForm] = useState({ mcpEnabled: false, rolloutPhase: 'pilot', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { tenants: list } = await portalMcpApi.listTenants(search || undefined);
      setTenants(list);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (t: McpTenantFeature) => {
    setEditing(t);
    setForm({
      mcpEnabled: t.mcpEnabled,
      rolloutPhase: t.rolloutPhase || 'pilot',
      notes: t.notes || '',
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await portalMcpApi.updateTenant(editing.tenantId, form);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <McpAdminPageShell title="Tenant MCP 기능" description="Tenant별 MCP 활성화·Rollout Phase">
      {error && <Alert variant="error" title="오류" description={error} />}
      <Input
        placeholder="tenantId 검색..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>MCP</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead>Catalog</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.map((t) => (
              <TableRow key={t.tenantId}>
                <TableCell>
                  <div className="font-medium">{t.displayName}</div>
                  <div className="text-xs text-muted-foreground">{t.tenantId}</div>
                </TableCell>
                <TableCell>{t.mcpEnabled ? 'ON' : 'OFF'}</TableCell>
                <TableCell>{t.rolloutPhase}</TableCell>
                <TableCell>{t.catalogVersion}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)}>
                    편집
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tenant MCP — {editing?.tenantId}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <Label>MCP 활성화</Label>
              <Switch checked={form.mcpEnabled} onCheckedChange={(v) => setForm((f) => ({ ...f, mcpEnabled: v }))} />
            </div>
            <div className="space-y-2">
              <Label>Rollout Phase</Label>
              <Select value={form.rolloutPhase} onValueChange={(v) => setForm((f) => ({ ...f, rolloutPhase: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pilot">pilot</SelectItem>
                  <SelectItem value="phase1">phase1</SelectItem>
                  <SelectItem value="phase2">phase2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>메모</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving}>
              저장
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </McpAdminPageShell>
  );
}
